import React, { useEffect } from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from 'react-modal';
import DatePicker, { registerLocale } from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { taskApi } from '../../../apis/task/taskApi';
import { formatISO8601 } from '../../../libs/utils/formatDate';
import { Task } from '../../../types/types';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

registerLocale('ja', ja);

interface UpdateTaskCalendarModalProps {
  onSubmitted: () => Promise<void>;
  taskList: Task;
}

export const UpdateTaskCalendarModal = (
  props: UpdateTaskCalendarModalProps
) => {
  const { onSubmitted, taskList } = props;
  const { success, danger } = useSystemMessage();
  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = React.useState<Date | undefined>(undefined);
  const [disabled, setDisabled] = React.useState<boolean>(false);
  const location = useLocation();
  const { goto } = useAppRouter();

  const handleDateChange = (dates: Date | [Date, Date] | null) => {
    if (!Array.isArray(dates)) {
      return;
    }
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleOpen = () => {
    if (taskList?.startTime === null || taskList?.endTime === null) {
      setStartDate(new Date());
      setEndDate(new Date());
      return;
    }
    setStartDate(new Date(taskList.startTime));
    setEndDate(new Date(taskList.endTime));
  };

  useEffect(() => {
    handleOpen();
    // eslint-disable-next-line
  }, [taskList]);

  const handleClose = async () => {
    setStartDate(undefined);
    setEndDate(undefined);
    await onSubmitted();
  };

  const handleClear = async () => {
    try {
      await taskApi.update(
        taskList.id,
        {
          name: taskList.name,
          startTime: null,
          endTime: null,
        },
        {}
      );
      setStartDate(undefined);
      setEndDate(undefined);
      await success('タスク期間をクリアしました');
      await onSubmitted();
    } catch(e) {
      await logError(
        location.pathname,
        'タスク期間のクリアに失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  const handleSubmit = async () => {
    setDisabled(true);

    if (startDate === undefined || endDate === undefined) {
      await danger('開始時刻または終了時刻が選択されていません。');
      setDisabled(false);
      return;
    }

    try {
      await taskApi.update(
        taskList.id,
        {
          name: taskList.name,
          startTime: formatISO8601(startDate),
          endTime: formatISO8601(endDate),
        },
        {}
      );
      setDisabled(false);
      await success('タスク期間を設定しました');
      await onSubmitted();
    } catch(e) {
      await logError(
        location.pathname,
        'タスク期間の更新に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={handleClose}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">期間設定</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={handleClose}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <div className="form-modal-content flex justify-center w-full">
            <DatePicker
              locale="ja"
              selected={startDate || new Date()}
              onChange={handleDateChange}
              onInputClick={() => setEndDate(undefined)}
              startDate={startDate}
              endDate={endDate}
              shouldCloseOnSelect={false}
              monthsShown={2}
              fixedHeight
              selectsRange
              disabledKeyboardNavigation
              inline
            />
          </div>
          <div className="form-modal-footer-container">
            <div className="form-modal-footer">
              <button className="btn-sub" type="button" onClick={handleClear}>
                クリア
              </button>
              {!endDate ? (
                <button className="btn-primary-disabled" type="button" disabled>
                  保存
                </button>
              ) : (
                <button
                  className="btn-primary"
                  type="button"
                  onClick={handleSubmit}
                  disabled={disabled}
                >
                  {disabled ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-4"
                      />
                      保 存
                    </>
                  ) : (
                    '保　存'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};
